import { useLocalStorage } from '@uidotdev/usehooks';
import { Resizable } from 're-resizable';
import { memo, useCallback, useLayoutEffect, useRef } from 'react';

import { merge } from '@/components';
import { DocumentForm, DocumentFormHeader, DocumentTableForm, DocumentToolbar, DocumentViewer } from '@/components/ui';
import { DocumentProvider, useGetAgentRunFromPath, useMeasuredSize, useRemainingDaysOfTrial } from '@/hooks';
import { InboxProvider, useValidationStore, ValidationProvider } from '@/store';

import { DetailsAgentRunsPanelWrapper } from './DetailsAgentRunsPanelWrapper.tsx';
import { DetailsEmailPanelWrapper } from './DetailsEmailPanelWrapper.tsx';
import { ReviewPredictionsTooltip } from './ReviewPredictionsTooltip.tsx';

const IValidate = () => {
  const { data: run } = useGetAgentRunFromPath();

  const detachTable = useValidationStore((state) => state.detachTable);
  const setDetachTable = useValidationStore((state) => state.setDetachTable);
  const tableViewIsHidden = useValidationStore((state) => state.tableViewIsHidden);
  const setParentContainerSize = useValidationStore((state) => state.setParentContainerSize);

  const { hasExpired } = useRemainingDaysOfTrial();

  const [tableSize, setTableSize] = useLocalStorage('cradl:defaultTableHeight', '30vh');
  const tableRef = useRef<Resizable>(null);

  const onResize = useCallback(() => {
    if (!detachTable) {
      setDetachTable?.(true);
    }
    if (tableRef.current) {
      setTableSize(tableRef.current.size.height.toString());
    }
  }, [detachTable, setDetachTable, setTableSize]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const { width: containerWidth, height: containerHeight, onRef: onContainerRef } = useMeasuredSize(containerRef);

  useLayoutEffect(() => {
    if (containerWidth != null && containerHeight != null) {
      setParentContainerSize(containerWidth, containerHeight);
    }
  }, [containerHeight, containerWidth, setParentContainerSize]);

  return (
    <InboxProvider agentId={run?.agentId}>
      <div className="flex h-full w-full bg-gray-200">
        <div className="flex min-h-0 bg-white">
          <DetailsAgentRunsPanelWrapper agentId={run?.agentId} />
          <DetailsEmailPanelWrapper agentId={run?.agentId} runId={run?.runId} />
        </div>

        <DocumentProvider key={run?.runId}>
          <Resizable className="h-full" enable={{ right: true }} defaultSize={{ width: '60vw' }}>
            <div className="flex w-full justify-center">
              <div>
                <DocumentToolbar
                  className={merge('fixed z-10 h-14 -translate-x-[50%]', hasExpired ? 'top-12' : 'top-0')}
                />
              </div>
            </div>

            <div className="flex h-full flex-col" ref={onContainerRef}>
              <DocumentViewer />

              <Resizable
                ref={tableRef}
                className={merge('shadow-t-md bg-white', tableViewIsHidden && 'hidden')}
                defaultSize={{ height: tableSize }}
                enable={{ top: true }}
                onResize={onResize}
                aria-hidden={detachTable ? undefined : tableViewIsHidden}
              >
                <div className="flex h-full overflow-auto">
                  <DocumentTableForm className="w-full p-2" />
                </div>
              </Resizable>
            </div>
          </Resizable>

          <div className="flex-grow bg-white">
            <ReviewPredictionsTooltip offset={16} className="bottom-0 -translate-y-4 self-end" placement="right top" />

            <DocumentFormHeader className={merge('z-10 h-14 -translate-y-14', hasExpired ? 'top-12' : 'top-0')} />

            <DocumentForm className="h-full -translate-y-14 overflow-auto" />
          </div>
        </DocumentProvider>
      </div>
    </InboxProvider>
  );
};

const IValidationWrapper = () => {
  return (
    <ValidationProvider>
      <IValidate />
    </ValidationProvider>
  );
};

export const Validate = memo(IValidationWrapper);
